import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { CoreComponent, View } from '../../../types/forge';
import { AssetPicker } from '../../assets/AssetPicker';
import { InteractionsForm } from '../interactions/InteractionsForm';
import { saveView } from '../save-pipeline/savePipeline';
import { useEditorStore } from '../store/editorStore';
import { NavigatorRoutesEditor } from './NavigatorRoutesEditor';

const NAVIGATOR_TYPES = new Set(['TabNavigator', 'StackNavigator']);

interface AppData {
  FrontendApp_by_pk: { id: string; name: string };
  View: View[];
}

interface Palette {
  CoreComponent: CoreComponent[];
}

/**
 * Right-side property panel — data-driven from CoreComponent / DynamicPropertyDefinition.
 *
 * Mirrors Burrow's `PropertiesPanel` + `ViewStyleFormDynamic`. Phase 2
 * renders form fields based on the property definitions for the selected
 * view's CoreComponent.
 */
export function PropertyPanel({ appData, palette }: { appData: AppData; palette: Palette }) {
  const selectedId = useEditorStore((s) => s.selectedViewId);
  const tab = useEditorStore((s) => s.rightPanel);
  const setTab = useEditorStore((s) => s.setRightPanel);
  const appId = appData.FrontendApp_by_pk?.id;

  if (!selectedId) {
    return (
      <section style={styles.section}>
        <h3 style={styles.heading}>Inspector</h3>
        <p style={styles.empty}>Select a view to edit its properties.</p>
      </section>
    );
  }

  const view = appData.View.find((v) => v.id === selectedId);
  if (!view) {
    return (
      <section style={styles.section}>
        <h3 style={styles.heading}>Inspector</h3>
        <p style={styles.empty}>View not found.</p>
      </section>
    );
  }

  const coreComponent = palette.CoreComponent.find((c) => c.type === view.Type);

  return (
    <section style={styles.section}>
      <h3 style={styles.heading}>{view.Type} · {view.name}</h3>
      <div style={styles.tabs}>
        {(['properties', 'styles', 'interactions'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'properties' && (
        <>
          {/*
           * For navigators we lead the Properties tab with a dedicated
           * routes editor so authors don't have to drag Screens into the
           * tree by hand and select each one to rename / set an icon.
           * The generic property form (just `Name` + source for non-data
           * views) still renders below for completeness.
           */}
          {NAVIGATOR_TYPES.has(view.Type) && (
            <NavigatorRoutesEditor
              appId={appId}
              navigator={view}
              appViews={appData.View}
            />
          )}
          <PropertiesForm appId={appId} view={view} core={coreComponent} />
        </>
      )}
      {tab === 'styles' && <StylesForm appId={appId} view={view} core={coreComponent} />}
      {tab === 'interactions' && <InteractionsForm appId={appId} viewId={view.id} />}
    </section>
  );
}

function PropertiesForm({
  appId,
  view,
  core,
}: {
  appId: string;
  view: View;
  core?: CoreComponent;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(view.name);
  const [sourceValue, setSourceValue] = useState(view.viewSource?.value ?? '');
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    setName(view.name);
    setSourceValue(view.viewSource?.value ?? '');
  }, [view.id, view.name, view.viewSource?.value]);

  const mutate = useMutation({
    mutationFn: () =>
      saveView({
        viewId: view.id,
        patch: {
          name,
          viewSource: view.viewSource
            ? { ...view.viewSource, value: sourceValue }
            : { id: '', type: 'Static', value: sourceValue, url: null, method: null, query: null, variables: null, headers: null, refresh_interval_seconds: null },
        },
      }),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
    },
  });

  const sourceDef = core?.DynamicPropertyDefinitions.find((d) => d.key === 'source_value');
  // Image / Icon's source_value is a URL — wire it into the AssetPicker
  // so authors can pick from the org's CMS instead of pasting URLs.
  // Each picks from a different kind so authors aren't shown irrelevant
  // assets: `Image` views see image+icon assets; `Icon` views see icons
  // (the picker always also shows generic 'image' uploads as a fallback,
  // see AssetPicker for that policy).
  // Other data-bound types (Text / Button label / TextInput initial
  // value) stay free-form because their source is text + ${...}.
  const assetSourceKind: 'image' | 'icon' | null =
    sourceDef?.key === 'source_value'
      ? view.Type === 'Image'
        ? 'image'
        : view.Type === 'Icon'
          ? 'icon'
          : null
      : null;
  const isAssetSource = assetSourceKind !== null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutate.mutate();
      }}
      style={styles.form}
    >
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} style={styles.input} />
      </Field>

      {sourceDef && (
        <Field label={sourceDef.display_name}>
          {isAssetSource ? (
            <ImageSourceField
              value={sourceValue}
              onChange={setSourceValue}
              onPick={() => setPickerOpen(true)}
            />
          ) : (
            <input
              value={sourceValue}
              onChange={(e) => setSourceValue(e.target.value)}
              placeholder='e.g. "Hello, ${Env.userName}"'
              style={styles.input}
            />
          )}
        </Field>
      )}

      <button type="submit" disabled={mutate.isPending} style={styles.saveBtn}>
        {mutate.isPending ? 'Saving…' : 'Save'}
      </button>
      {mutate.isError && (
        <p style={styles.error}>{String((mutate.error as Error).message)}</p>
      )}

      {isAssetSource && (
        <AssetPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          kind={assetSourceKind}
          title={assetSourceKind === 'icon' ? 'Select Icon' : 'Select Image'}
          onPick={(url) => setSourceValue(url)}
        />
      )}
    </form>
  );
}

/**
 * Image source field — image preview + pick / clear controls + a
 * collapsed URL row for power users who want to paste an external URL
 * (or a `${Env.…}` expression) directly.
 */
function ImageSourceField({
  value,
  onChange,
  onPick,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: () => void;
}) {
  return (
    <div style={imgStyles.root}>
      <div style={imgStyles.row}>
        {value ? (
          <img
            src={value}
            alt="preview"
            style={imgStyles.thumb}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.opacity = '0.2';
            }}
          />
        ) : (
          <div style={imgStyles.thumbEmpty}>no image</div>
        )}
        <div style={imgStyles.controls}>
          <button type="button" onClick={onPick} style={imgStyles.pickBtn}>
            {value ? 'Replace…' : 'Pick from library…'}
          </button>
          {value && (
            <button type="button" onClick={() => onChange('')} style={imgStyles.clearBtn}>
              Clear
            </button>
          )}
        </div>
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://… or ${Env.imageUrl}"
        style={imgStyles.url}
      />
    </div>
  );
}

const imgStyles: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', gap: 6 },
  row: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  thumb: {
    width: 64,
    height: 64,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid #CBD5E1',
    backgroundColor: '#F8FAFC',
    flexShrink: 0,
  },
  thumbEmpty: {
    width: 64,
    height: 64,
    borderRadius: 6,
    border: '1px dashed #CBD5E1',
    backgroundColor: '#F8FAFC',
    color: '#94A3B8',
    fontSize: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  controls: { display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' },
  pickBtn: {
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 600,
    color: '#4F46E5',
    backgroundColor: '#fff',
    border: '1px solid #C7D2FE',
    borderRadius: 4,
    cursor: 'pointer',
  },
  clearBtn: {
    padding: '2px 8px',
    fontSize: 11,
    color: '#64748B',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  url: {
    padding: '6px 8px',
    fontSize: 11,
    color: '#475569',
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    fontFamily: 'ui-monospace, monospace',
  },
};

function StylesForm({
  appId,
  view,
  core,
}: {
  appId: string;
  view: View;
  core?: CoreComponent;
}) {
  const queryClient = useQueryClient();
  const initialProps = (view.viewStyle?.properties ?? {}) as Record<string, unknown>;
  const [props, setProps] = useState<Record<string, unknown>>(initialProps);

  useEffect(() => setProps(initialProps), [view.id]);

  const styleDefs =
    core?.DynamicPropertyDefinitions.filter((d) => d.SubCategory.Category.name === 'Styles') ?? [];

  // Strip undefined / null / empty-string values and normalize dimension
  // keys before sending to the server. Empty enum strings (e.g.
  // flexDirection="") reach Yoga as native parse errors and cause RN to
  // drop the entire style. Empty values on non-enum keys (width="",
  // backgroundColor="") are ambiguous noise the engine drops at runtime
  // anyway, so we don't persist them either.
  //
  // Dimension keys also need coercion: authors copy/paste CSS values like
  // "300px" or type bare numerics like "70". Yoga only accepts JSON
  // numbers (DIPs) or strings of the form "<n>%". Normalizing here keeps
  // the stored data clean and consistent with the engine's own validator.
  const cleanProps = (raw: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v === undefined || v === null) continue;
      if (typeof v === 'string' && v.trim() === '') continue;

      if (DIMENSION_KEYS.has(k) || NUMERIC_KEYS.has(k)) {
        const normalized = normalizeDimensionValue(k, v);
        if (normalized === null) continue;
        out[k] = normalized;
        continue;
      }

      out[k] = v;
    }
    return out;
  };

  const mutate = useMutation({
    mutationFn: () => {
      const cleaned = cleanProps(props);
      return saveView({
        viewId: view.id,
        patch: {
          viewStyle: view.viewStyle
            ? { ...view.viewStyle, properties: cleaned }
            : { id: '', properties: cleaned },
        },
      });
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
    },
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); mutate.mutate(); }} style={styles.form}>
      {styleDefs.length === 0 && <p style={styles.empty}>No style properties for this type.</p>}
      {styleDefs
        .slice()
        .sort((a, b) => a.SubCategory.priority - b.SubCategory.priority)
        .map((def) => (
          <Field key={def.id} label={def.display_name} hint={def.SubCategory.name}>
            <StyleInput def={def} props={props} setProps={setProps} />
          </Field>
        ))}
      <button type="submit" disabled={mutate.isPending} style={styles.saveBtn}>
        {mutate.isPending ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}

/**
 * Render the appropriate input control for a `DynamicPropertyDefinition`.
 *
 * Critical: enum-typed defs MUST render as a <select> driven by
 * `def.options.values`. The previous free-text input let authors type
 * unsupported strings (e.g. flexDirection="flex", position="fixed") which
 * crashed Yoga at native render time and caused the RN shell to drop the
 * entire style — making the shell render look nothing like the builder.
 */
function StyleInput({
  def,
  props,
  setProps,
}: {
  def: { key: string; type: string; options: unknown };
  props: Record<string, unknown>;
  setProps: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const current = props[def.key];

  if (def.type === 'enum') {
    const values = extractEnumValues(def.options);
    return (
      <select
        value={typeof current === 'string' ? current : ''}
        onChange={(e) => {
          const v = e.target.value;
          // Empty selection = "use platform default". Strip the key
          // entirely instead of persisting an empty string (which Yoga
          // can't parse on the native side).
          setProps((prev) => {
            const next = { ...prev };
            if (v === '') delete next[def.key];
            else next[def.key] = v;
            return next;
          });
        }}
        style={styles.input}
      >
        <option value="">— default —</option>
        {values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    );
  }

  if (def.type === 'number') {
    return (
      <input
        type="number"
        value={typeof current === 'number' ? current : ''}
        onChange={(e) => {
          const raw = e.target.value;
          setProps((prev) => {
            const next = { ...prev };
            if (raw === '') delete next[def.key];
            else next[def.key] = Number(raw);
            return next;
          });
        }}
        style={styles.input}
      />
    );
  }

  if (def.type === 'color') {
    const value = typeof current === 'string' && current ? current : '#000000';
    return (
      <input
        type="color"
        value={value}
        onChange={(e) => setProps((prev) => ({ ...prev, [def.key]: e.target.value }))}
        style={styles.input}
      />
    );
  }

  // Some defs declared as `type='string'` in the seed are actually
  // dimensions (width/height/etc.) that accept a number-of-DIPs OR a
  // "<n>%" string. Render them as a text box but show a placeholder
  // hinting at the accepted formats; final coercion happens on save.
  if (DIMENSION_KEYS.has(def.key)) {
    const display =
      typeof current === 'number'
        ? String(current)
        : typeof current === 'string'
          ? current
          : '';
    return (
      <input
        type="text"
        value={display}
        onChange={(e) => {
          const v = e.target.value;
          setProps((prev) => {
            const next = { ...prev };
            if (v === '') delete next[def.key];
            else next[def.key] = v;
            return next;
          });
        }}
        placeholder="e.g. 200, 50%, auto"
        style={styles.input}
      />
    );
  }

  // Free-form string (`string`, `sourceValue`, anything unrecognized).
  return (
    <input
      type="text"
      value={typeof current === 'string' ? current : ''}
      onChange={(e) => {
        const v = e.target.value;
        setProps((prev) => {
          const next = { ...prev };
          if (v === '') delete next[def.key];
          else next[def.key] = v;
          return next;
        });
      }}
      style={styles.input}
    />
  );
}

/**
 * Style keys that accept a Yoga dimension: number (DIPs) or "<n>%".
 * A few also accept "auto" — handled inside `normalizeDimensionValue`.
 *
 * Keep this in sync with the engine's `DIMENSION_KEYS` set in
 * forge-rendering-engine/src/core/style/propertyMap.ts.
 */
const DIMENSION_KEYS = new Set([
  'width',
  'height',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
  'top',
  'right',
  'bottom',
  'left',
  'start',
  'end',
  'padding',
  'paddingHorizontal',
  'paddingVertical',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'margin',
  'marginHorizontal',
  'marginVertical',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'gap',
  'rowGap',
  'columnGap',
  'flexBasis',
  'borderRadius',
  'borderWidth',
  'fontSize',
  'lineHeight',
  'letterSpacing',
  'shadowRadius',
  'shadowOffsetWidth',
  'shadowOffsetHeight',
]);

const NUMERIC_KEYS = new Set([
  'flex',
  'flexGrow',
  'flexShrink',
  'aspectRatio',
  'opacity',
  'shadowOpacity',
  'zIndex',
  'elevation',
]);

const AUTO_ALLOWED_KEYS = new Set([
  'margin',
  'marginHorizontal',
  'marginVertical',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
]);

/**
 * Normalize a dimension/numeric input into its persisted form:
 *   - "<n>%" strings are kept as strings.
 *   - "auto" is kept (only on keys that allow it).
 *   - "300px" / "70" / "  12 " / numbers all become a JS number.
 *   - Anything unsalvageable returns `null` (caller drops the key).
 */
function normalizeDimensionValue(key: string, value: unknown): number | string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed === 'auto') return AUTO_ALLOWED_KEYS.has(key) ? 'auto' : null;
  if (/^-?\d+(?:\.\d+)?%$/.test(trimmed)) {
    return NUMERIC_KEYS.has(key) ? null : trimmed;
  }
  const pxMatch = /^(-?\d+(?:\.\d+)?)\s*px$/i.exec(trimmed);
  const numericPart = pxMatch ? pxMatch[1] : trimmed;
  const num = Number(numericPart);
  return Number.isFinite(num) ? num : null;
}

/**
 * `DynamicPropertyDefinition.options` is a free-form jsonb. We accept a
 * couple of shapes so existing rows + future shapes both work:
 *   - `{ values: ['row', 'column', ...] }`   (canonical)
 *   - `['row', 'column', ...]`               (bare array)
 * Anything else degrades to no-options (the user just sees "— default —").
 */
function extractEnumValues(options: unknown): string[] {
  if (Array.isArray(options)) {
    return options.filter((v): v is string => typeof v === 'string');
  }
  if (options && typeof options === 'object' && 'values' in options) {
    const values = (options as { values: unknown }).values;
    if (Array.isArray(values)) {
      return values.filter((v): v is string => typeof v === 'string');
    }
  }
  return [];
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={styles.field}>
      <span style={styles.labelText}>
        {label}
        {hint && <span style={styles.labelHint}> · {hint}</span>}
      </span>
      {children}
    </label>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { padding: 16 },
  heading: { fontSize: 13, fontWeight: 600, color: '#0F172A', margin: '0 0 12px' },
  empty: { fontSize: 12, color: '#94A3B8' },
  tabs: { display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #E2E8F0' },
  tab: {
    padding: '6px 10px', fontSize: 11, fontWeight: 500, color: '#64748B',
    backgroundColor: 'transparent', border: 'none', borderBottom: '2px solid transparent',
    cursor: 'pointer', textTransform: 'capitalize',
  },
  tabActive: { color: '#4F46E5', borderBottomColor: '#4F46E5' },
  form: { display: 'flex', flexDirection: 'column', gap: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  labelText: { fontSize: 11, fontWeight: 600, color: '#475569' },
  labelHint: { fontWeight: 400, color: '#94A3B8' },
  input: {
    padding: '6px 8px', fontSize: 12, border: '1px solid #CBD5E1', borderRadius: 4,
  },
  saveBtn: {
    marginTop: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600,
    color: '#fff', backgroundColor: '#4F46E5', border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  error: { fontSize: 11, color: '#991B1B', marginTop: 4 },
};
